import { useEffect, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  formatIngredientSectors,
  formatYieldInput,
  normalizeIngredientSectors,
  normalizeYieldInput,
} from "../utils/ingredientFormatters";

const columns = [
  ["", "38px"],
  ["Insumo", "minmax(150px,1.4fr)"],
  ["Categoría", "minmax(120px,1fr)"],
  ["Sector", "minmax(145px,1.1fr)"],
  ["Unidad de consumo", "minmax(120px,1fr)"],
  ["Rendimiento", "minmax(115px,.8fr)"],
  ["Control de stock", "minmax(115px,.9fr)"],
  ["Presentaciones", "minmax(115px,.9fr)"],
  ["Acciones", "minmax(105px,.75fr)"],
];
const template = columns.map(([, width]) => width).join(" ");

function Cell({ children }) {
  return <Typography noWrap title={typeof children === "string" ? children : undefined} sx={{ color: "primary.900", fontSize: 12.5, fontWeight: 700, textAlign: "center" }}>{children}</Typography>;
}

function StockButton({ ingredient, disabled, editable, onToggle }) {
  const active = Boolean(ingredient.isStockActive);
  return (
    <Button
      disabled={disabled || !editable}
      onClick={() => onToggle?.(ingredient)}
      sx={{
        minWidth: 96,
        borderRadius: "20px",
        bgcolor: active ? "secondary.500" : "neutral.300",
        color: active ? "logoblanco" : "primary.900",
        textTransform: "none",
        fontSize: 12,
        fontWeight: 800,
        "&:hover": { bgcolor: active ? "secondary.600" : "neutral.400" },
        "&.Mui-disabled": {
          bgcolor: active ? "secondary.500" : "neutral.300",
          color: active ? "logoblanco" : "neutral.700",
          opacity: 0.75,
        },
      }}
    >
      {active ? "Activo" : "Inactivo"}
    </Button>
  );
}

function YieldEditor({ ingredient, disabled, onCommit }) {
  const [value, setValue] = useState(() => formatYieldInput(ingredient.defaultYieldFactor));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    setValue(formatYieldInput(ingredient.defaultYieldFactor));
    setInvalid(false);
  }, [ingredient.defaultYieldFactor]);

  async function commit() {
    const saved = await onCommit?.(ingredient, value);
    setInvalid(saved === false);
  }

  return (
    <TextField
      size="small"
      value={value}
      error={invalid}
      disabled={disabled}
      onChange={(event) => {
        setValue(normalizeYieldInput(event.target.value));
        setInvalid(false);
      }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
      inputProps={{ "aria-label": `Rendimiento de ${ingredient.name}`, inputMode: "decimal", maxLength: 13, style: { textAlign: "right" } }}
      sx={{ width: 112, bgcolor: "neutral.50", "& .MuiOutlinedInput-root": { borderRadius: "18px" } }}
    />
  );
}

export default function InsumosTable({
  rows = [],
  sectorOptions = [],
  editingId = null,
  loading = false,
  togglingId = null,
  updatingSectorId = null,
  updatingYieldId = null,
  hidingId = null,
  selectedIds = [],
  onToggleSelected,
  onToggleVisible,
  onToggleEdit,
  onToggleStock,
  onChangeSectors,
  onChangeYield,
  onOpenPresentations,
  onRequestRemove,
}) {
  const visibleIds = rows.map((row) => Number(row.id));
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.includes(id));

  return (
    <Box sx={{ flex: 1, minHeight: 0, borderRadius: "20px", overflow: "hidden" }}>
      <Box sx={{ height: "100%", overflow: "auto" }}>
        <Box sx={{ minWidth: 1120, minHeight: "100%", bgcolor: "neutral.50" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: template, gap: 1, alignItems: "center", minHeight: 48, px: 2, py: 0.5, bgcolor: "neutral.600", position: "sticky", top: 0, zIndex: 3 }}>
            {columns.map(([label], index) => index === 0 ? (
              <Checkbox
                key="selection"
                size="small"
                checked={allVisibleSelected}
                indeterminate={!allVisibleSelected && someVisibleSelected}
                disabled={loading || !visibleIds.length}
                onChange={() => onToggleVisible?.(visibleIds, !allVisibleSelected)}
                inputProps={{ "aria-label": "Seleccionar insumos visibles" }}
                sx={{ p: 0.25, color: "logoblanco", "& .MuiSvgIcon-root": { fontSize: 19 }, "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "logoblanco" } }}
              />
            ) : <Typography key={label} sx={{ color: "logoblanco", fontSize: 12.5, fontWeight: 800, textAlign: "center" }}>{label}</Typography>)}
          </Box>

          {rows.map((ingredient) => {
            const editing = editingId === ingredient.id;
            const currentSectors = normalizeIngredientSectors(ingredient.sector);
            const presentations = Number(ingredient.activePurchaseItemCount || 0);
            return (
              <Box key={ingredient.id} sx={{ display: "grid", gridTemplateColumns: template, gap: 1, alignItems: "center", minHeight: 58, px: 2, py: 1, borderBottom: "1px solid rgba(0,0,0,.06)", bgcolor: editing ? "secondary.50" : "white", "&:hover": { bgcolor: editing ? "secondary.50" : "rgba(0,0,0,.025)" } }}>
                <Checkbox
                  size="small"
                  checked={selectedIds.includes(Number(ingredient.id))}
                  onChange={() => onToggleSelected?.(Number(ingredient.id))}
                  inputProps={{ "aria-label": `Seleccionar ${ingredient.name}` }}
                  sx={{ p: 0.25, justifySelf: "center", "& .MuiSvgIcon-root": { fontSize: 19 } }}
                />
                <Cell>{ingredient.name}</Cell>
                <Cell>{ingredient.category}</Cell>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {editing ? (
                    <Select
                      multiple
                      size="small"
                      value={currentSectors}
                      disabled={loading || updatingSectorId === ingredient.id}
                      onChange={(event) => onChangeSectors?.(ingredient, event.target.value)}
                      renderValue={(selected) => selected.join(", ")}
                      inputProps={{ "aria-label": `Sector de ${ingredient.name}` }}
                      sx={{ width: "100%", maxWidth: 190, bgcolor: "neutral.50", borderRadius: "18px", fontSize: 12.5, fontWeight: 700 }}
                    >
                      {sectorOptions.map((sector) => (
                        <MenuItem key={sector} value={sector}>
                          <Checkbox checked={currentSectors.includes(sector)} />
                          <ListItemText primary={sector} />
                        </MenuItem>
                      ))}
                    </Select>
                  ) : <Cell>{ingredient.sectorLabel || formatIngredientSectors(ingredient.sector)}</Cell>}
                </Box>
                <Cell>{ingredient.baseUnitLabel}</Cell>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {editing ? <YieldEditor ingredient={ingredient} disabled={loading || updatingYieldId === ingredient.id} onCommit={onChangeYield} /> : <Cell>{ingredient.yieldLabel}</Cell>}
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <StockButton ingredient={ingredient} editable={editing} disabled={loading || togglingId === ingredient.id} onToggle={onToggleStock} />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => onOpenPresentations?.(ingredient)}
                  sx={{
                    minWidth: 122,
                    justifySelf: "center",
                    px: 1.25,
                    py: 0.45,
                    borderRadius: "18px",
                    borderColor: presentations ? "primary.500" : "warning.main",
                    bgcolor: presentations ? "rgba(24, 58, 146, 0.04)" : "rgba(237, 108, 2, 0.04)",
                    color: presentations ? "primary.900" : "warning.dark",
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.2,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      borderColor: presentations ? "primary.700" : "warning.dark",
                      bgcolor: presentations ? "rgba(24, 58, 146, 0.1)" : "rgba(237, 108, 2, 0.1)",
                    },
                  }}
                >
                  {presentations ? `${presentations} ${presentations === 1 ? "presentación" : "presentaciones"}` : "Sin presentación"}
                </Button>
                <Stack direction="row" spacing={0.5} sx={{ justifyContent: "center" }}>
                  <Tooltip title={editing ? "Finalizar edición" : "Editar"}>
                    <span>
                      <IconButton size="small" disabled={loading} aria-label={`${editing ? "Finalizar edición" : "Editar"} ${ingredient.name}`} onClick={() => onToggleEdit?.(ingredient)}>
                        {editing ? <CheckIcon sx={{ fontSize: 19, color: "primary.900" }} /> : <EditIcon sx={{ fontSize: 18, color: "primary.900" }} />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Quitar de la lista">
                    <span>
                      <IconButton size="small" disabled={loading || hidingId === ingredient.id} aria-label={`Quitar de la lista ${ingredient.name}`} onClick={() => onRequestRemove?.(ingredient)}>
                        <CloseIcon sx={{ fontSize: 19, color: "error.main" }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Box>
            );
          })}

          {!rows.length ? <Typography sx={{ p: 3, color: "neutral.700", fontWeight: 700 }}>{loading ? "Cargando insumos..." : "No se encontraron insumos."}</Typography> : null}
        </Box>
      </Box>
    </Box>
  );
}
